// Analysis views for the Apple 2703(d) Parser. The views are the shared ones in
// components/analysis/views.js with Apple's wording; the case summary PDF has
// Apple-specific sections (accounts, devices, iOS versions).

import React from 'react';
import {
  PatternOfLifeView as PatternOfLife, IncidentView as Incident, LinksView, SecurityView as Security, CaseSummaryView as CaseSummary,
  ipHistory, downloadPdf,
} from '../analysis/views';
import { GENERATED } from './ui';
import { modelName } from '../../utils/apple2703d';

export { HomeWorkCards, IpScreening, screeningLabel, screeningFlagged } from '../analysis/views';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ORDER = [1, 2, 3, 4, 5, 6, 0];

export const PatternOfLifeView = props => (
  <PatternOfLife {...props} explain="Each square counts records from activity logs (iCloud, iMessage/FaceTime lookups, sign-ins, purchases, downloads, mail logs, Game Center) in that hour of that weekday, in the time zone picked at the top. Darker means busier." />
);

export const IncidentView = props => (
  <Incident {...props} modelName={modelName} handleLabel="Looked up (iMessage / FaceTime)"
    explain="Pick the time of the incident (in the time zone chosen at the top) and how wide a window to look at; every sheet is searched." />
);

export const AccountLinksView = props => (
  <LinksView {...props} what="accounts in this return"
    explain="A shared device here means the same serial number or device ID. Shared contacts are numbers or emails both accounts looked up; the accounts' own numbers and emails are left out." />
);

export const SecurityView = props => (
  <Security {...props} sub="Password resets and account changes, failed sign-ins, billing set-up and account creation."
    explain="These are the moments an account's access or identity changed. Apple ID / iForgot activity covers Apple's account-management and password-reset sites."
    emptyText="No security events in this return (no iForgot / My Apple ID activity, failed sign-ins or billing records)." />
);

export const CaseSummaryView = props => (
  <CaseSummary {...props} explain="One document for the case file: each account's details, likely home and work networks, the top IP addresses (with VPN/Tor screening if you ran it on the IP page), top contacts, devices and iOS versions, the pattern-of-life heatmap, the longest activity gaps, security events and links between accounts. It follows the account picker at the top too." />
);

export async function downloadCaseSummary(s) {
  await downloadPdf(caseSummaryDoc(s), `Apple 2703d case summary ${new Date().toISOString().slice(0, 10)}.pdf`);
}

// The PDF's content, kept separate from the download so it can be checked on its own.
export function caseSummaryDoc(s) {
  const H = text => ({ text, fontSize: 13, bold: true, color: '#0f172a', margin: [0, 14, 0, 6] });
  const small = text => ({ text, fontSize: 8.5, color: '#64748b', margin: [0, 0, 0, 4] });
  const table = (headers, rows, widths) => (rows.length ? {
    table: { headerRows: 1, widths: widths || headers.map(() => '*'), body: [headers.map(h => ({ text: h, bold: true, fontSize: 8.5, fillColor: '#e2e8f0' })), ...rows.map(r => r.map(c => ({ text: String(c ?? ''), fontSize: 8.5 })))] },
    layout: 'lightHorizontalLines', margin: [0, 0, 0, 6],
  } : { text: 'None.', fontSize: 9, color: '#64748b' });

  const content = [
    { text: 'Apple Subpoena / 2703(d) Return: Case Summary', fontSize: 18, bold: true },
    { text: `${s.scope} · Generated ${s.fmt(Date.now())} · Times in ${s.tzLabel}`, fontSize: 9, color: '#64748b', margin: [0, 2, 0, 8] },
    { text: `* ${GENERATED} Everything in this summary was compiled and calculated by LE Tech Tools from the files Apple produced.`, fontSize: 9, bold: true, color: '#b45309', margin: [0, 0, 0, 4] },
    { text: 'Review aid only. Always refer to the actual returns from Apple. This summary does not replace the original data, and anything relied on in a report, affidavit or court must be checked against the original files. Items marked "likely" are inferences with the evidence shown, not facts.', fontSize: 9, color: '#92400e', margin: [0, 0, 0, 6] },
    H('Accounts'),
  ];
  for (const a of s.accounts) {
    const get = k => ((a.profile || []).find(([h]) => h.toLowerCase() === k.toLowerCase()) || [])[1] || '';
    const rows = [
      ['Account', s.accountLabel(a.key)], ['Name', a.name], ['Apple ID', a.appleId || a.emails[0]], ['DSID', a.dsids.join(', ')],
      ['Phones', [get('FaceTime / iMessage Phone'), get('Verified Phone'), ...a.phones].filter(Boolean).join(', ')],
      ['Address', [get('Address Line 1'), get('City'), get('Postal Code')].filter(Boolean).join(', ')],
      ['Status', get('Account Status')], ['Apple ID created', get('Apple ID Creation Date')], ['Registration IP', get('Registration IP Address')],
      ['iCloud services on', (a.services || []).filter(([, on]) => /^yes|^on|^true/i.test(on)).map(([n]) => n).join(', ')],
    ].filter(([, v]) => v);
    content.push({ table: { widths: [110, '*'], body: rows.map(([k, v]) => [{ text: k, fontSize: 8.5, color: '#64748b' }, { text: String(v), fontSize: 8.5 }]) }, layout: 'noBorders', margin: [0, 0, 0, 8] });
  }

  content.push(H('Likely home and work networks'));
  [['Likely home', s.homeWork.home], ['Likely work', s.homeWork.work]].forEach(([k, g]) => content.push({
    text: g ? `${k}: ${g.label} (seen on ${g.nights} nights and ${g.workdays} weekday working days, ${g.count} records)` : `${k}: none seen on 3 or more days.`,
    fontSize: 9, margin: [0, 0, 0, 3],
  }));
  content.push(H('Top IP addresses'));
  content.push(table(['IP', 'Records', 'Days', 'Nights', 'First', 'Last', 'Screening'],
    s.ips.slice(0, 15).map(r => [r.ip, r.count, r.days, r.nights, s.fmt(r.first), s.fmt(r.last), s.screen(r.ip)]), ['*', 38, 30, 34, 85, 85, 80]));

  content.push(H('Top contacts (iMessage / FaceTime lookups)'));
  content.push(small('Ranked by the number of different days a handle was looked up. A lookup is not proof of a message or call.'));
  content.push(table(['Handle', 'Days', 'Lookups', 'First', 'Last', 'Service'],
    s.contacts.slice(0, 15).map(c => [c.handle, c.days, c.count, s.fmt(c.first), s.fmt(c.last), c.services.join(', ')]), ['*', 30, 40, 85, 85, 60]));

  content.push(H('Devices'));
  content.push(table(['Serial', 'IMEI', 'Product', 'Seen in'], s.serials.slice(0, 12).map(d => [d.serial, d.imei.join(', '), d.description.join(', '), d.sources.join(', ')])));
  content.push(table(['Model', 'iOS', 'First', 'Last', 'Records'], s.deviceTl.slice(0, 15).map(d => [`${d.model}${d.name ? ` (${d.name})` : ''}`, d.os, s.fmt(d.first), s.fmt(d.last), d.count]), ['*', 90, 85, 85, 40]));

  content.push(H('Pattern of life'));
  content.push(small(`Activity records per weekday and hour (${s.tzLabel}). Darker = busier.`));
  const max = s.pol.max;
  content.push({
    table: {
      widths: [24, ...new Array(24).fill('*')],
      body: [
        ['', ...Array.from({ length: 24 }, (_, h) => ({ text: h % 3 === 0 ? String(h) : '', fontSize: 6, alignment: 'center' }))],
        ...ORDER.map(wd => [{ text: DAYS[wd], fontSize: 7 }, ...s.pol.grid[wd].map(n => ({
          text: n ? String(n) : '', fontSize: 5.5, alignment: 'center', color: n / max > 0.55 ? '#ffffff' : '#1e3a8a',
          fillColor: n ? `#${[59, 130, 246].map(c => Math.round(255 - (255 - c) * (0.15 + 0.85 * n / max)).toString(16).padStart(2, '0')).join('')}` : '#f8fafc',
        }))]),
      ],
    },
    layout: { hLineWidth: () => 0.5, vLineWidth: () => 0.5, hLineColor: () => '#ffffff', vLineColor: () => '#ffffff' },
  });
  content.push(H('Longest activity gaps'));
  content.push(table(['From', 'To', 'Length'], s.gaps.slice(0, 10).map(g => [s.fmt(g.from), s.fmt(g.to), g.hours >= 48 ? `${(g.hours / 24).toFixed(1)} days` : `${g.hours.toFixed(1)} hours`])));

  content.push(H('Account security events'));
  content.push(table(['Time', 'Account', 'Event', 'IP', 'IP history'], s.security.slice(0, 25).map(e => [s.fmt(e.t), s.accountLabel(e.account), e.type, e.ip, ipHistory(e)]), [85, '*', 110, 90, 50]));

  if (s.links.length) {
    content.push(H('Links between accounts'));
    s.links.slice(0, 10).forEach(l => content.push({
      text: `${s.accountLabel(l.a)} and ${s.accountLabel(l.b)}: ${l.devices.length} shared devices, ${l.networks.length} shared networks, ${l.ips.length} shared IPs, ${l.contacts.length} shared contacts`,
      fontSize: 9, margin: [0, 0, 0, 3],
    }));
  }
  content.push(H('Source files'));
  content.push(small(s.files.join('\n')));

  return {
    pageMargins: [36, 40, 36, 40], defaultStyle: { font: 'Roboto', fontSize: 9 }, content,
    footer: (page, pages) => ({ text: `* Generated by LE Tech Tools. Not part of the original legal return. Review aid only: refer to the original Apple returns. · Page ${page} of ${pages}`, fontSize: 7, color: '#94a3b8', alignment: 'center', margin: [0, 10, 0, 0] }),
  };
}
